import type { ConsultReportPayload } from '../hooks/use-consult-reports'

interface ConsultReportViewProps {
  payload: ConsultReportPayload
  visitLabel?: string
  visitAt?: string | null
  showDisclaimerBanner?: boolean
}

export function ConsultReportView({
  payload,
  visitLabel,
  visitAt,
  showDisclaimerBanner = false,
}: ConsultReportViewProps) {
  const talkingPoints = payload.talking_points ?? []
  const questions = payload.questions_for_practitioner ?? []

  return (
    <div className="space-y-6">
      {showDisclaimerBanner && (
        <div
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
        >
          Patient-prepared for a consultation — not clinical advice.
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-xl font-semibold tracking-tight">
          {payload.headline || visitLabel || 'Consultation preparation'}
        </h2>
        {(visitLabel || visitAt) && (
          <p className="text-sm text-muted-foreground">
            {visitLabel ? <span>{visitLabel}</span> : null}
            {visitLabel && visitAt ? ' · ' : null}
            {visitAt ? <span>{new Date(visitAt).toLocaleString()}</span> : null}
          </p>
        )}
      </div>

      {payload.narrative ? (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Summary</h3>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
            {payload.narrative}
          </div>
        </section>
      ) : null}

      {talkingPoints.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Talking points</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {talkingPoints.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {questions.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Questions for the practitioner</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {questions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {payload.disclaimer ? (
        <p className="text-xs text-muted-foreground">{payload.disclaimer}</p>
      ) : null}
    </div>
  )
}
